import { ActionType, TableColumn } from "../../../Component/Model/Table/Table";
import { DefaultCell } from "../../../Component/Common/Components/DataTable/DefaultCell";
import { ActionsCell } from "../../../Component/Common/Components/DataTable/ActionsCell";
import {RequestTracker} from "../../../Component/Model/Dto/Staging/RequestTracker";
import {StringDateTimeHelper} from "../../../Component/Model/helpers";
import {useRecoilValue} from "recoil";
import {userRolesState} from "../../../State/user.Atom";
import {EE123PeopleRole_Edit, PeopleEditRole} from "../../../Component/Common/Constants/UserRoles";
import {RequestStatus, ValidationStatus} from "../../../Component/Model/Enum/ImportEnum";
import {Box, Chip} from "@mui/material";

// Validation is a separate property of a fetch; surface it as a small badge next to the fetch status (nothing shown
// when validation has not run). Failed (the data was judged and rows failed) is red; CouldNotComplete (the run
// itself crashed or died, nothing was judged) is amber.
const ValidationBadge = ({validationStatus, label}: { validationStatus: number, label: string }) => {
    if (validationStatus === ValidationStatus.NotValidated) {
        return null;
    }

    let color: "success" | "error" | "warning" | "info";
    if (validationStatus === ValidationStatus.Passed) {
        color = "success";
    } else if (validationStatus === ValidationStatus.Failed) {
        color = "error";
    } else if (validationStatus === ValidationStatus.CouldNotComplete) {
        color = "warning";
    } else {
        color = "info";
    }

    return <Chip size="small" label={label} color={color} variant="outlined"/>;
};

interface RequestTrackerRows {
    payrollName: string;
    createdOn: string;
    statusDescription: string;
    validationStatus: number;
    validationStatusDescription: string;
    completedOn: string;
    requestUsername: string;
    actions: ActionType[];
}

interface RequestTrackerTable {
    columns: Array<TableColumn>;
    rows: Array<RequestTrackerRows>;
}

export function RequestTrackerTableMapper(dataRows: RequestTracker.SearchDto[], openPayrollImport: () => void, openReview: (requestId: number) => void, onRetriggerValidation: (requestId: number) => void): RequestTrackerTable {

    const userRoles = useRecoilValue(userRolesState);

    const columns = [
        { Header: 'Created Date', accessor: 'createdOn', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Payroll(s)', accessor: 'payrollName', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Status', accessor: 'statusDescription', Cell: ({ row }: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DefaultCell value={row.original.statusDescription} />
                <ValidationBadge validationStatus={row.original.validationStatus} label={row.original.validationStatusDescription} />
            </Box>
        ), disableSortBy: true },
        { Header: 'Completed', accessor: 'completedOn', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Fetched By', accessor: 'requestUsername', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];

    if (dataRows && dataRows.length > 0) {
        const rows = dataRows.map((item: RequestTracker.SearchDto) => {
            const payrollNames = item.payrollNames?.length
                ? item.payrollNames.filter(Boolean).join(", ")
                : "";

            // Import needs a completed fetch (ImportReady) and the right role. Validation is advisory only (the badge
            // + review screen) and never gates the import - the real import validates every row itself.
            const canImport = item.status === RequestStatus.ImportReady &&
                userRoles.some(r => r === EE123PeopleRole_Edit || r === PeopleEditRole);

            const canReview = (item.validationStatus === ValidationStatus.Passed || item.validationStatus === ValidationStatus.Failed)
                && item.hasValidationReview;

            // Re-run the dry-run validation on demand (needs a completed fetch and the edit role); hidden while a
            // validation is already running for this request.
            const canRevalidate = item.status === RequestStatus.ImportReady &&
                item.validationStatus !== ValidationStatus.Validating &&
                userRoles.some(r => r === EE123PeopleRole_Edit || r === PeopleEditRole);

            return {
                payrollName: payrollNames,
                createdOn: StringDateTimeHelper(item.createdOn.toString(), true),
                statusDescription: item.statusDescription,
                validationStatus: item.validationStatus,
                validationStatusDescription: item.validationStatusDescription,
                requestUsername: item.requestUsername,
                completedOn: StringDateTimeHelper(item.completedOn?.toString() ?? "", true),
                actions: [
                    ...(canReview
                        ? [{
                            key: item.requestId.toString(),
                            label: 'Review validation',
                            icon: { name: 'fact_check', color: 'info' },
                            action: (id: string) => openReview(Number(id))
                        }]
                        : []),
                    ...(canRevalidate
                        ? [{
                            key: item.requestId.toString(),
                            label: 'Re-run validation',
                            icon: { name: 'refresh', color: 'info' },
                            action: (id: string) => onRetriggerValidation(Number(id))
                        }]
                        : []),
                    ...(canImport
                        ? [{
                            key: payrollNames,
                            label: 'Import',
                            icon: { name: 'cloud_upload', color: 'info' },
                            action: openPayrollImport
                        }]
                        : [])
                ]
            };
        });

        return { columns, rows };
    } else {
        return { columns, rows: [] };
    }
}